import type { PrimeMemberByPublicIdFn } from '@/features/iam'
import { type Job } from '@/features/jobs/Job'
import {
  type JobProgressionStepStatus,
  type OverrideableJobProgressionStepStatus,
} from '@/features/jobs/taxes/progression/JobProgression'
import { type JobProgressionStore } from '@/features/jobs/taxes/progression/JobProgressionStore'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import {
  flowStepOrder,
  getFlowStepsDetails,
  getOverrideableProgressionSteps,
  getProgressionStepDetails,
  getProgressionStepTitle,
  stepsShownWhenCurrent,
  stepsShownWhenReached,
} from '@/features/jobs/taxes/progression/progression-content'
import { StartProgressionDialogViewModel } from '@/features/jobs/details/components/JobProgressionCard/NoProgressionActions/StartProgressionDialog/StartProgressionDialogViewModel'
import { type DocumentCreationModalViewModel } from '@/features/documents/DocumentCreationModal/DocumentCreationModalViewModel'
import { OpenItemsDialogViewModel } from '@/features/jobs/details/components/JobProgressionCard/OpenItemsProgressionActions/OpenItemsDialog/OpenItemsDialogViewModel'
import type {
  WellKnownDocumentTypeShortId,
  DocumentType,
} from '@/features/documents/DocumentType'
import type { Document } from '@/features/documents/Document'
import { distinctUntilChanged, filter, merge, switchMap } from 'rxjs'
import {
  format,
  formatDistanceStrict,
  formatDistanceToNowStrict,
} from 'date-fns'
import { type AvailableJobProgressionFlowsStore } from '@/features/jobs/taxes/progression/available-job-progression-flows/AvailableJobProgressionFlowsStore'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { JobProgressionRequestDraftApprovalViewModel } from '@/features/jobs/details/components/JobProgressionCard/JobProgressionRequestDraftApprovalModal/JobProgressionRequestDraftApprovalViewModel'
import { EstimatedIrsPaymentDialogViewModel } from '@/features/jobs/details/components/JobProgressionCard/EstimatedIrsPaymentDialog/EstimatedIrsPaymentDialogViewModel'
import {
  FinalReturnAmountsDialogViewModel,
  returnAmountsToYearInputs,
} from '@/features/jobs/details/components/FinalReturnAmountsDialog/FinalReturnAmountsDialogViewModel'
import { type JobStore } from '@/features/jobs/JobStore'
import { type JobScopeChangeStore } from '@/features/jobs/job-scope-changes/JobScopeChangeStore'
import {
  JobProgressionHistoryViewModel,
  type JobProgressionHistoryActor,
} from '@/features/jobs/details/components/JobProgressionCard/JobProgressionHistoryModal/JobProgressionHistoryViewModel'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { ApproveDraftInReviewDialogViewModel } from '@/features/jobs/details/components/ApproveDraftInReviewDialog/ApproveDraftInReviewDialogViewModel'
import { RejectDraftInReviewDialogViewModel } from '@/features/jobs/details/components/RejectDraftInReviewDialog/RejectDraftInReviewDialogViewModel'
import { UpdateProgressionStepDialogViewModel } from '@/features/jobs/details/components/UpdateProgressionStepDialog/UpdateProgressionStepDialogViewModel'
import { memberRoleLabel } from '@/features/jobs/details/member-role-label'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { withAbortSignal } from '@/utils/rx-util'
import { toStream } from '@/utils/mobx-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { Revalidation } from '@/infra/revalidation'

type JobProgressionDocumentCreationView = 'ReturnFiled' | 'Extension'

export interface FlowTimelineStep {
  status: JobProgressionStepStatus
  title: string
  proDescription: string
  clientDescription: string
  state: 'done' | 'current' | 'upcoming'
  reachedAt: Date | null
  reachedBy: JobProgressionHistoryActor | null
}

/**
 * A tax job's progression flow: where the job is, what each step asks of the
 * pro and the client, and the dialogs that move it along.
 */
export class JobProgressionViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _revalidation = new Revalidation(() => this.fetch.pending)

  /**
   * The upload modal of the step being answered, if any.
   */
  @observable
  accessor jobProgressionDocumentCreationVm: DocumentCreationModalViewModel | null =
    null

  /**
   * Which step the upload modal answers.
   */
  @observable
  accessor documentCreationToggledView: JobProgressionDocumentCreationView | null =
    null

  /**
   * Dialog for requesting draft approval.
   */
  readonly requestDraftReturnApprovalVm: JobProgressionRequestDraftApprovalViewModel

  /**
   * Dialog listing the progression step history.
   */
  readonly historyDialogVm: JobProgressionHistoryViewModel

  /**
   * Dialog explaining every step of the flow and where the job is in it.
   */
  readonly stepsDialogVm = new ModalViewModel()

  /**
   * Dialog for setting job progression to job started.
   */
  readonly startProgressionDialogVm: StartProgressionDialogViewModel

  /**
   * Dialog for settings job progression to open items
   */
  readonly openItemsDialogVm: OpenItemsDialogViewModel

  /**
   * Dialog for entering estimated IRS payment when accepting an extension.
   */
  readonly estimatedIrsPaymentDialogVm: EstimatedIrsPaymentDialogViewModel

  /**
   * Dialog for approving the draft in review.
   */
  readonly approveDraftInReviewDialogVm: ApproveDraftInReviewDialogViewModel

  /**
   * Dialog for rejecting the draft in review.
   */
  readonly rejectDraftInReviewDialogVm: RejectDraftInReviewDialogViewModel

  /**
   * Dialog for overriding the current progression step.
   */
  readonly updateProgressionStepDialogVm: UpdateProgressionStepDialogViewModel

  /**
   * Dialog for entering per-year final return amounts on return-filed.
   */
  readonly finalReturnAmountsDialogVm: FinalReturnAmountsDialogViewModel

  constructor(
    private readonly _progressionStore: JobProgressionStore,
    private readonly _availableJobProgressionFlowsStore: AvailableJobProgressionFlowsStore,
    private readonly _requestJobClose: () => void,
    private readonly _createDocumentCreationModalViewModel: () => DocumentCreationModalViewModel,
    private readonly _getJobDocuments: () => Document[],
    private readonly _promptForDocumentRequest: () => Promise<
      DocumentType[] | null
    >,
    public readonly job: Job,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _jobStore: JobStore,
    private readonly _jobScopeChangeStore: JobScopeChangeStore,
    private readonly _primeMemberByPublicId: PrimeMemberByPublicIdFn,
  ) {
    this.historyDialogVm = new JobProgressionHistoryViewModel(
      () => this.progression,
      getProgressionStepTitle,
      (userPublicId) =>
        this._primeMemberByPublicId({
          workspaceId: this.job.workspace.id,
          userPublicId,
        }),
    )

    this.requestDraftReturnApprovalVm =
      new JobProgressionRequestDraftApprovalViewModel(
        this._createDocumentCreationModalViewModel,
      )

    this.startProgressionDialogVm = new StartProgressionDialogViewModel(
      this._progressionStore,
      this.job,
      this._createAlertErrorHandler,
    )

    this.openItemsDialogVm = new OpenItemsDialogViewModel(
      this._progressionStore,
      this._promptForDocumentRequest,
      this.job,
      this._createAlertErrorHandler,
    )

    this.estimatedIrsPaymentDialogVm = new EstimatedIrsPaymentDialogViewModel(
      this._progressionStore,
      this.job,
      this._createAlertErrorHandler,
    )

    this.approveDraftInReviewDialogVm = new ApproveDraftInReviewDialogViewModel(
      this._progressionStore,
      this.job,
      this._createAlertErrorHandler,
    )

    this.rejectDraftInReviewDialogVm = new RejectDraftInReviewDialogViewModel(
      this._progressionStore,
      this.job,
      this._createAlertErrorHandler,
    )

    this.updateProgressionStepDialogVm =
      new UpdateProgressionStepDialogViewModel(
        this,
        this._createAlertErrorHandler,
      )

    this.finalReturnAmountsDialogVm = new FinalReturnAmountsDialogViewModel(
      this._jobStore,
      this.job,
      this._createAlertErrorHandler,
      () => this._completeReturnFiledAfterAmounts(),
      () => returnAmountsToYearInputs(this.job.returnAmounts),
    )

    this._lifecycle.manage(
      merge(
        this._progressionStore.updated$.pipe(
          filter((jobId) => jobId === this.job.id),
        ),
        // A scope change or a status change can switch the job to another flow.
        merge(
          this._jobScopeChangeStore.jobScopeChangeApproved$,
          this._jobScopeChangeStore.adminJobScopeUpdated$,
        ).pipe(filter((e) => e.jobId === this.job.id)),
        toStream(() => this.job.status).pipe(distinctUntilChanged()),
      ).pipe(
        switchMap(() =>
          withAbortSignal((signal) =>
            this._createAlertRetryWrapper(
              'Loading the progression failed',
              signal,
            )(() =>
              Promise.all([
                this._progressionStore.fetchProgression(this.job.id, signal),
                this._availableJobProgressionFlowsStore.fetchAvailableJobProgressionFlows(
                  this.job.id,
                  signal,
                ),
              ]),
            )
              // A failed refetch must not end the stream.
              .catch(() => undefined),
          ),
        ),
      ),
    )
  }

  /**
   * Whether the progression is refetching while the previous one stays on
   * screen.
   */
  @computed
  get isRevalidating() {
    return this._revalidation.isRevalidating
  }

  /**
   * Whether the document creation toggled view is ReturnFiled.
   */
  @computed
  get isDocumentCreationToggledViewReturnFiled() {
    return this.documentCreationToggledView === 'ReturnFiled'
  }

  /**
   * Whether the document creation toggled view is Extension.
   */
  @computed
  get isDocumentCreationToggledViewExtension() {
    return this.documentCreationToggledView === 'Extension'
  }

  /**
   * Progression steps for the current job.
   */
  @computed
  get progression() {
    return this._progressionStore.progressionForJob(this.job.id)
  }

  /**
   * Available job progression flows for the current job.
   */
  @computed
  get availableJobProgressionFlows() {
    return this._availableJobProgressionFlowsStore.availableProgressionFlowsForJob(
      this.job.id,
    )
  }

  /**
   * Whether personal extension progression flow is available.
   */
  @computed
  get isPersonalExtensionAvailable() {
    return (
      this.availableJobProgressionFlows?.availableInventoryItemTaxExtensionFlows
        ?.personalExtensionProgressionFlowAvailable === true
    )
  }

  /**
   * Whether business extension progression flow is available.
   */
  @computed
  get isBusinessExtensionAvailable() {
    return (
      this.availableJobProgressionFlows?.availableInventoryItemTaxExtensionFlows
        ?.businessExtensionProgressionFlowAvailable === true
    )
  }

  /**
   * Whether outsourcing extension progression flow is available.
   */
  @computed
  get isOutsourcingExtensionAvailable() {
    return (
      this.availableJobProgressionFlows?.availableInventoryItemTaxExtensionFlows
        ?.outsourcingExtensionProgressionFlowAvailable === true
    )
  }

  /**
   * Whether any extension progression flow is available.
   */
  @computed
  get isAnyExtensionFlowAvailable() {
    return (
      this.isPersonalExtensionAvailable ||
      this.isBusinessExtensionAvailable ||
      this.isOutsourcingExtensionAvailable
    )
  }

  /**
   * Whether the current progression flow is an extension flow.
   */
  @computed
  get isExtensionFlow() {
    return (
      this.progression?.progressionFlow === 'PersonalExtension' ||
      this.progression?.progressionFlow === 'BusinessExtension' ||
      this.progression?.progressionFlow === 'OutsourcingExtension'
    )
  }

  /**
   * Steps of the job's progression.
   */
  @computed
  get progressionSteps() {
    return this.progression?.steps
  }

  /**
   * Whether any progression step has been recorded.
   */
  @computed
  get hasProgressionHistory() {
    return (this.progressionSteps?.length ?? 0) > 0
  }

  /**
   * Status of the first unanswered progression step.
   */
  @computed
  get jobProgressionStepStatus() {
    return this.progressionSteps?.find((s) => s.response === null)?.status
  }

  /**
   * The current progression step status.
   */
  @computed
  get currentStepStatus() {
    if (this.isExtensionFlow) {
      return this.jobProgressionStepStatus ?? 'ExtensionNotStarted'
    }

    return this.jobProgressionStepStatus ?? 'NotStarted'
  }

  /**
   * Whether the job progression flow has been started or not.
   */
  @computed
  get hasStarted() {
    if (this.isExtensionFlow) {
      return this.currentStepStatus !== 'ExtensionNotStarted'
    }

    return this.currentStepStatus !== 'NotStarted'
  }

  /**
   * Whether the extension was rejected at any step.
   */
  @computed
  get containsExtensionRejected() {
    return Boolean(
      this.progressionSteps?.some((s) => s.status === 'ExtensionRejected'),
    )
  }

  /**
   * Whether the extension was rejected.
   */
  @computed
  get isExtensionRejected() {
    return this.currentStepStatus === 'ExtensionRejected'
  }

  /**
   * Whether an extension rejection confirmation was uploaded.
   */
  @computed
  get submittedExtensionConfirmationRejection() {
    return this._getJobDocuments()
      .map((d) => d.documentType?.id)
      .some((shortId) => shortId === 'EXTENSION_REJECTION_CONFIRMATION')
  }

  /**
   * The job's unarchived draft return document.
   */
  @computed
  get draftReturnDocument() {
    return this._getJobDocuments().find(
      (d) => d.documentType?.id === 'DRAFT_RETURN' && !d.archived,
    )
  }

  /**
   * Whether a draft return exists in the documents for the job.
   */
  @computed
  get hasDraftReturn() {
    return Boolean(this.draftReturnDocument)
  }

  /**
   * Whether a draft return is downloadable for the job.
   */
  @computed
  get isDraftReturnDownloadable() {
    return Boolean(this.draftReturnDocument?.downloadable)
  }

  /**
   * Whether the extension was rejected or the job waits on the client.
   */
  @computed
  get isExtensionRejectedOrWaitingOnClient() {
    return (
      this.currentStepStatus === 'ExtensionRejected' ||
      this.currentStepStatus === 'ProgressionWaitingOnClient'
    )
  }

  /**
   * Whether the current progression flow is Outsourcing Extension.
   */
  @computed
  get isOutsourcingExtension() {
    return this.currentProgressionFlow === 'OutsourcingExtension'
  }

  /**
   * Is current step status DRAFT_APPROVED
   */
  @computed
  get isCurrentStepStatusDraftApproved() {
    return this.currentStepStatus === 'DraftApproved'
  }

  /**
   * The current percentage of the progression flow completed.
   */
  @computed
  get completionStatus() {
    return this.job.completed
      ? 100
      : (this.currentStepInfo?.progress?.completed ?? 0)
  }

  /**
   * The current percentage of the progression flow pending completion.
   */
  @computed
  get pendingCompletionStatus() {
    return this.job.completed
      ? 0
      : (this.currentStepInfo?.progress?.pending ?? 0)
  }

  /**
   * The current progression flow.
   */
  @computed
  get currentProgressionFlow() {
    return this.progression?.progressionFlow ?? 'None'
  }

  /**
   * Returns details for the current progression step.
   */
  @computed
  get currentStepInfo() {
    return getProgressionStepDetails(
      this.currentProgressionFlow,
      this.currentStepStatus,
      this.progressionSteps,
    )
  }

  /**
   * The flow's steps in order, as the client portal (arphyx) counts them:
   * detours like open items or a rejected draft only while they are the
   * current step, and the rarer steps only once the job has reached them.
   */
  @computed
  get flowSteps(): { status: JobProgressionStepStatus; title: string }[] {
    const details = getFlowStepsDetails(this.currentProgressionFlow)
    const current = this.currentStepStatus
    const reached = new Set(this.progressionSteps?.map((s) => s.status))
    return flowStepOrder.flatMap((status) => {
      const detail = details[status]
      if (!detail) {
        return []
      }
      if (stepsShownWhenCurrent.includes(status) && status !== current) {
        return []
      }
      if (stepsShownWhenReached.includes(status) && !reached.has(status)) {
        return []
      }
      return [{ status, title: detail.title }]
    })
  }

  /**
   * Whether the current flow lists any steps.
   */
  @computed
  get hasFlowSteps() {
    return this.flowSteps.length > 0
  }

  /**
   * Where the current step sits in {@link flowSteps}; -1 when the flow
   * doesn't list it.
   */
  @computed
  get currentFlowStepIndex() {
    return this.flowSteps.findIndex((s) => s.status === this.currentStepStatus)
  }

  /**
   * When the job entered its current step.
   */
  @computed
  get currentStepSince(): Date | null {
    return this._lastReached(this.currentStepStatus)?.dateCreated ?? null
  }

  /**
   * {@link flowSteps} with what each step asks of the pro and the client, and
   * when and by whom the job reached it.
   */
  @computed
  get flowTimeline(): FlowTimelineStep[] {
    const details = getFlowStepsDetails(this.currentProgressionFlow)
    const current = this.currentFlowStepIndex
    const completed = this.job.completed
    return this.flowSteps.map((step, index) => {
      const reached = this._lastReached(step.status)
      return {
        ...step,
        proDescription: details[step.status]?.proDescription ?? '',
        clientDescription: details[step.status]?.clientDescription ?? '',
        state:
          completed || index < current
            ? 'done'
            : index === current
              ? 'current'
              : 'upcoming',
        reachedAt: reached?.dateCreated ?? null,
        reachedBy: reached?.createdByUserPublicId
          ? this._toActor(reached.createdByUserPublicId)
          : null,
      }
    })
  }

  /**
   * How long the job has sat in its current step and where that step falls in
   * the flow.
   */
  get currentStepSummary() {
    if (this.job.completed) {
      return ''
    }
    return [
      this.currentStepSince &&
        `for ${formatDistanceToNowStrict(this.currentStepSince)}`,
      this.currentFlowStepIndex >= 0 &&
        `Step ${this.currentFlowStepIndex + 1} of ${this.flowSteps.length}`,
    ]
      .filter(Boolean)
      .join(' · ')
  }

  /**
   * What the current step asks of the pro and of the client.
   */
  @computed
  get currentStepDescriptions() {
    const step = this.flowTimeline[this.currentFlowStepIndex]
    return {
      pro: step?.proDescription ?? '',
      client: step?.clientDescription ?? '',
    }
  }

  /**
   * How the current step reads: a detour the job has to come back from, or an
   * extension the IRS turned down.
   */
  @computed
  get currentStepTone(): 'default' | 'warning' | 'danger' {
    switch (this.currentStepStatus) {
      case 'OpenItems':
      case 'DraftRejected':
        return 'warning'
      case 'ExtensionRejected':
        return 'danger'
      default:
        return 'default'
    }
  }

  /**
   * Where the job is in its flow and how long ago it was submitted.
   */
  get stepsDialogDescription() {
    return [
      this.currentFlowStepIndex >= 0 &&
        `Step ${this.currentFlowStepIndex + 1} of ${this.flowSteps.length}`,
      this.job.dateSubmitted &&
        `${formatDistanceToNowStrict(this.job.dateSubmitted)} since the job was submitted`,
    ]
      .filter(Boolean)
      .join(' · ')
  }

  /**
   * Whether the document creation modal's upload button is disabled.
   */
  @computed
  get isUploadDisabled() {
    if (!this.jobProgressionDocumentCreationVm) {
      return true
    }

    // If you can skip upload, enable upload button only if the Pro has uploaded documents.
    if (!this.isSkipUploadDisabled) {
      return !this.jobProgressionDocumentCreationVm.documentCreationItemVms
        .length
    }

    return !this.jobProgressionDocumentCreationVm.documentCreationItemVms
      .map((i) => i.selectedType?.id)
      .some((shortId) =>
        this.permittedDocumentTypesForFlow.includes(
          shortId as WellKnownDocumentTypeShortId,
        ),
      )
  }

  /**
   * Whether the document creation modal's skip button is disabled.
   */
  @computed
  get isSkipUploadDisabled() {
    // Review notes are optional for the review progression flow.
    if (this.currentProgressionFlow === 'Review') {
      return false
    }

    const jobDocuments = this._getJobDocuments()

    return !jobDocuments
      .map((d) => d.documentType?.id)
      .some((shortId) =>
        this.permittedDocumentTypesForFlow.includes(
          shortId as WellKnownDocumentTypeShortId,
        ),
      )
  }

  /**
   * Document types that satisfy the current flow's upload step.
   */
  @computed
  get permittedDocumentTypesForFlow() {
    switch (this.currentProgressionFlow) {
      case 'Retail':
        return [
          'FINAL_RETURN',
          'FINAL_RETURN_TO_BE_MAILED',
          'EXTENSION_CONFIRMATION',
        ]
      case 'Essential':
        return ['ANNOTATED_SOURCE_DOCUMENT']
      case 'Preparation':
        return ['PREP_NOTES']
      case 'Review':
        return ['REVIEW_NOTES']
      case 'PersonalExtension':
      case 'BusinessExtension':
        if (this.isExtensionRejected) {
          return ['EXTENSION_REJECTION_CONFIRMATION']
        }

        return ['EXTENSION_CONFIRMATION']
      default:
        return []
    }
  }

  /**
   * Steps an admin can move the progression to.
   */
  @computed
  get overrideableProgressionSteps() {
    return getOverrideableProgressionSteps(
      this.currentProgressionFlow,
      this.currentStepStatus,
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The progression card renders a failure from the task's state.
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.fetch.reset()
  }

  /**
   * Loads the job's progression and the flows it can switch to.
   */
  fetch = task(async (signal: AbortSignal) => {
    await this._progressionStore.fetchProgression(this.job.id, signal)
    await this._availableJobProgressionFlowsStore.fetchAvailableJobProgressionFlows(
      this.job.id,
      signal,
    )
    this._revalidation.markLoaded()
  })

  /**
   * {@link flowTimeline} as the steps dialog lists it, described for the
   * client or the pro. Not computed: the current step's duration runs to now.
   */
  stepsDialogSteps(forClient: boolean) {
    const timeline = this.flowTimeline
    return timeline.map((step, index) => {
      const reachedAt = step.state !== 'upcoming' ? step.reachedAt : null
      // A finished step lasted until the next one began; the current one is
      // still running.
      const until =
        step.state === 'current' ? new Date() : timeline[index + 1]?.reachedAt
      const duration =
        reachedAt && until ? formatDistanceStrict(reachedAt, until) : null
      return {
        id: step.status,
        title: step.title,
        description: forClient ? step.clientDescription : step.proDescription,
        state: step.state,
        currentLabel:
          step.state === 'current'
            ? `Current${duration ? ` · ${duration}` : ''}`
            : null,
        meta: reachedAt
          ? [
              `${step.state === 'current' ? 'Since ' : ''}${format(reachedAt, 'MMM d, h:mm a')}`,
              step.reachedBy &&
                `${step.reachedBy.member.displayName}${step.reachedBy.roleLabel ? ` (${step.reachedBy.roleLabel})` : ''}`,
              step.state === 'done' && duration,
            ]
              .filter(Boolean)
              .join(' · ')
          : null,
      }
    })
  }

  /**
   * Closes the steps dialog and opens the progression history.
   */
  @action.bound
  viewHistoryFromSteps() {
    this.stepsDialogVm.close()
    this.historyDialogVm.modal.show()
  }

  /**
   * Sets the current progression to job started.
   */
  answerJobStarted = task.resolved(async () => {
    await this._progressionStore
      .transitionToStep(this.job.id, { step: 'JobStarted' })
      .catch(this._createAlertErrorHandler('Starting the job failed'))
  })

  /**
   * Sets the current progression to personal extension in progress.
   */
  answerJobPersonalExtensionInProgress = task.resolved(async () => {
    await this._progressionStore
      .transitionToStep(this.job.id, {
        step: 'ExtensionInProgress',
        flow: 'Personal',
      })
      .catch(
        this._createAlertErrorHandler('Starting the personal extension failed'),
      )
  })

  /**
   * Sets the current progression to business extension in progress.
   */
  answerJobBusinessExtensionInProgress = task.resolved(async () => {
    await this._progressionStore
      .transitionToStep(this.job.id, {
        step: 'ExtensionInProgress',
        flow: 'Business',
      })
      .catch(
        this._createAlertErrorHandler('Starting the business extension failed'),
      )
  })

  /**
   * Sets the current progression to outsourcing extension in progress.
   */
  answerJobOutsourcingExtensionInProgress = task.resolved(async () => {
    await this._progressionStore
      .transitionToStep(this.job.id, {
        step: 'ExtensionInProgress',
        flow: 'Outsourcing',
      })
      .catch(
        this._createAlertErrorHandler(
          'Starting the outsourcing extension failed',
        ),
      )
  })

  /**
   * Opens the estimated tax payment dialog for accepting an extension.
   */
  @action.bound
  answerJobExtensionAccepted() {
    this.estimatedIrsPaymentDialogVm.modal.show()
  }

  /**
   * Sets the current progression to extension rejected.
   */
  answerJobExtensionRejected = task.resolved(async () => {
    await this._progressionStore
      .transitionToStep(this.job.id, { step: 'ExtensionRejected' })
      .catch(this._createAlertErrorHandler('Rejecting the extension failed'))
  })

  /**
   * Sets the current progression to ready to file.
   */
  answerReadyToFile = task.resolved(async (setAsAdmin: boolean) => {
    await this._progressionStore
      .transitionToStep(this.job.id, { step: 'ReadyToFile', setAsAdmin })
      .catch(
        this._createAlertErrorHandler('Moving the job to ready to file failed'),
      )
  })

  /**
   * Shows the documents received dialog.
   */
  @action.bound
  showJobStartedDialog() {
    this.startProgressionDialogVm.modal.show()
  }

  /**
   * Starts reporting open items on the job.
   */
  @action.bound
  reportOpenItems() {
    // The dialog has already alerted the failure.
    this.openItemsDialogVm.show().catch(() => undefined)
  }

  /**
   * Sets the current progression to authorization signed.
   */
  answerAuthorizationSigned = task.resolved(async () => {
    await this._progressionStore
      .transitionToStep(this.job.id, { step: 'AuthorizationSigned' })
      .catch(this._createAlertErrorHandler('Signing the authorization failed'))
  })

  /**
   * Sets the current progression to return filed.
   */
  answerReturnFiled = task.resolved(async (skipDocumentUpload: boolean) => {
    if (!this.jobProgressionDocumentCreationVm) {
      return
    }

    if (!skipDocumentUpload) {
      await this.jobProgressionDocumentCreationVm.uploadFiles()
    }

    try {
      switch (this.currentProgressionFlow) {
        case 'Retail':
          if (
            (!skipDocumentUpload && this._hasFinalReturnUpload) ||
            this._hasFinalReturnDocument
          ) {
            // Gate: collect per-year final return amounts before advancing.
            // The upload (if any) already succeeded, so close the upload modal
            // first — leaving it open lists the files as still pending, and
            // re-submitting would upload duplicates. Cancelling the dialog
            // keeps the step unanswered; the uploaded document then matches
            // hasFinalReturnDocument, so re-entering through Skip reopens the
            // dialog without another upload.
            this.closeDocumentCreationModal()
            this.finalReturnAmountsDialogVm.show()
            return
          }
          await this._progressionStore.transitionToStep(this.job.id, {
            step: 'ReturnFiled',
          })
          break
        case 'Essential':
          await this._progressionStore.transitionToStep(this.job.id, {
            step: 'DeliverableCompleted',
          })
          break
        case 'Preparation':
          await this._progressionStore.transitionToStep(this.job.id, {
            step: 'PreparationCompleted',
          })
          break
        case 'Review':
          await this._progressionStore.transitionToStep(this.job.id, {
            step: 'ReviewCompleted',
          })
          break
      }
      this.closeDocumentCreationModal()
    } catch (err) {
      this._createAlertErrorHandler('Filing the return failed')(err)
    }
  })

  /**
   * Sets the current progression to extension confirmed.
   */
  answerExtensionConfirmed = task.resolved(
    async (skipDocumentUpload: boolean) => {
      if (!this.jobProgressionDocumentCreationVm) {
        return
      }

      if (!skipDocumentUpload) {
        await this.jobProgressionDocumentCreationVm.uploadFiles()
      }

      try {
        switch (this.currentProgressionFlow) {
          case 'PersonalExtension':
          case 'BusinessExtension':
            await this._progressionStore.transitionToStep(this.job.id, {
              step: 'ExtensionConfirmed',
            })
            break
        }
        this.closeDocumentCreationModal()
      } catch (err) {
        this._createAlertErrorHandler('Confirming the extension failed')(err)
      }
    },
  )

  /**
   * Uploads the extension rejected document.
   */
  uploadExtensionRejectedDocument = task.resolved(
    async (skipDocumentUpload: boolean) => {
      if (!this.jobProgressionDocumentCreationVm) {
        return
      }

      if (!skipDocumentUpload) {
        await this.jobProgressionDocumentCreationVm.uploadFiles()
      }

      this.closeDocumentCreationModal()
    },
  )

  /**
   * Overrides the current progression to specific step.
   */
  overrideProgressionStep = task.resolved(
    async (stepId: OverrideableJobProgressionStepStatus) => {
      await this._progressionStore.overrideProgressionStep(this.job.id, stepId)
    },
  )

  /**
   * Displays a modal to close out the current job.
   */
  @action.bound
  closeJob() {
    this._requestJobClose()
  }

  /**
   * Opens the document creation modal for the return-filed step.
   */
  @action.bound
  viewReturnFiledDocumentCreationModal() {
    this.documentCreationToggledView = 'ReturnFiled'
    this.jobProgressionDocumentCreationVm =
      this._createDocumentCreationModalViewModel()
    this.jobProgressionDocumentCreationVm.modal.show()
  }

  /**
   * Opens the document creation modal for the extension step.
   */
  @action.bound
  viewExtensionDocumentCreationModal() {
    this.documentCreationToggledView = 'Extension'
    this.jobProgressionDocumentCreationVm =
      this._createDocumentCreationModalViewModel()
    this.jobProgressionDocumentCreationVm.modal.show()
  }

  /**
   * Closes the document creation modal.
   */
  @action.bound
  closeDocumentCreationModal() {
    this.jobProgressionDocumentCreationVm?.modal.close()
    this.documentCreationToggledView = null
  }

  /**
   * Requests draft approval.
   */
  answerDraftInReview = task.resolved(async () => {
    if (
      this.progression?.progressionFlow !== 'Outsourcing' &&
      this.progression?.progressionFlow !== 'Review' &&
      !this.hasDraftReturn
    ) {
      this.requestDraftReturnApprovalVm.show()
      return
    }

    await this._progressionStore
      .transitionToStep(this.job.id, { step: 'DraftInReview' })
      .catch(this._createAlertErrorHandler('Requesting draft approval failed'))
  })

  /**
   * Whether the pending document upload includes a final return document
   * (Final Return / Final Return To Be Mailed), which gates the return-filed
   * step behind the final return amounts dialog.
   */
  private get _hasFinalReturnUpload() {
    return !!this.jobProgressionDocumentCreationVm?.documentCreationItemVms
      .map((i) => i.selectedType?.id)
      .some(
        (shortId) =>
          shortId === 'FINAL_RETURN' || shortId === 'FINAL_RETURN_TO_BE_MAILED',
      )
  }

  /**
   * Whether a final return document already exists on the job. Gates the
   * return-filed step behind the amounts dialog even with no new upload
   * pending, so a Pro who cancelled the dialog can re-enter through Skip
   * without re-uploading the file.
   */
  private get _hasFinalReturnDocument() {
    return this._getJobDocuments().some(
      (d) =>
        !d.archived &&
        (d.documentType?.id === 'FINAL_RETURN' ||
          d.documentType?.id === 'FINAL_RETURN_TO_BE_MAILED'),
    )
  }

  private _lastReached(status: JobProgressionStepStatus) {
    return (this.progressionSteps ?? [])
      .filter((s) => s.status === status && s.dateCreated)
      .sort((a, b) => b.dateCreated!.getTime() - a.dateCreated!.getTime())[0]
  }

  private _toActor(userPublicId: string): JobProgressionHistoryActor {
    const member = this._primeMemberByPublicId({
      workspaceId: this.job.workspace.id,
      userPublicId,
    })
    return {
      member,
      roleLabel: memberRoleLabel(member),
    }
  }

  /**
   * Advances the return-filed progression after the Pro submits the final
   * return amounts dialog. The upload modal was already closed when the
   * dialog was gated open in `answerReturnFiled`.
   */
  private async _completeReturnFiledAfterAmounts() {
    await this._progressionStore
      .transitionToStep(this.job.id, { step: 'ReturnFiled' })
      .catch(this._createAlertErrorHandler('Filing the return failed'))
  }
}
